import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditorJsOutput from 'editorjs-react-renderer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Section from 'components/Section';
import MediaThumb from 'components/MediaThumb';
import ConfirmableIconButton from 'components/ConfirmableIconButton';

import { PackageEditAccomodationHotelMutation } from '__generated__/PackageEditAccomodationHotelMutation.graphql';
import { PackageEditAccomodationHotel_package$key } from '__generated__/PackageEditAccomodationHotel_package.graphql';
import { useErrorHandler } from 'lib';

type Props = {
  package: PackageEditAccomodationHotel_package$key;
};

const PackageEditAccomodationHotel: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const pack = useFragment(
    graphql`
      fragment PackageEditAccomodationHotel_package on Package {
        id

        hotel {
          id
          name
          destination

          mainMedia {
            ...MediaThumb_media
          }

          descriptionText {
            activeBlock {
              data
            }
          }
        }
      }
    `,
    props.package
  );

  const [update] = useMutation<PackageEditAccomodationHotelMutation>(graphql`
    mutation PackageEditAccomodationHotelMutation(
      $id: ID!
      $input: UpdatePackageInput!
    ) {
      updatePackage(id: $id, input: $input) {
        ...PackageEditAccommodation_package
      }
    }
  `);

  const { id, hotel } = pack;

  const handleDeleteClick = useCallback(() => {
    update({
      variables: { id, input: { hotel: null } },
      ...errorHandler(),

      optimisticUpdater: store => {
        store.get(id)?.setValue(null, 'hotel');
      },
    });
  }, [errorHandler, id, update]);

  if (!hotel) return null;

  return (
    <Section title={hotel.name} subTitle={hotel.destination} id="hotel">
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ mr: 2 }}>
          <MediaThumb media={hotel.mainMedia} sx={{ width: '120px' }} />

          <Box sx={{ textAlign: 'center' }}>
            <IconButton
              size="small"
              color="primary"
              href={`/hotels/${hotel.id}`}>
              <EditIcon fontSize="small" color="inherit" />
            </IconButton>

            <ConfirmableIconButton size="small" onConfirm={handleDeleteClick}>
              <DeleteIcon fontSize="small" />
            </ConfirmableIconButton>
          </Box>
        </Box>

        <Box>
          {hotel.descriptionText?.activeBlock?.data && (
            <EditorJsOutput
              data={JSON.parse(hotel.descriptionText.activeBlock.data)}
            />
          )}
        </Box>
      </Box>
    </Section>
  );
};

export default PackageEditAccomodationHotel;
